import { action, makeObservable, observable, runInAction } from 'mobx';
import {
  AcceptInvitationResult,
  ApiError,
  BusinessInvitation,
  InvitationPreview,
} from '../../types';
import {
  businessService,
  invitationService,
  CreateInvitationRequest,
} from '../../services';

export class InvitationStore {
  /** Owner's outgoing invitations list. */
  invitations: BusinessInvitation[] = [];
  loading = false;
  sending = false;
  revoking = false;
  /** Preview / accept state for the invitation acceptance landing page. */
  preview: InvitationPreview | null = null;
  previewing = false;
  accepting = false;
  error: ApiError | null = null;

  constructor() {
    makeObservable(this, {
      invitations: observable,
      loading: observable,
      sending: observable,
      revoking: observable,
      preview: observable,
      previewing: observable,
      accepting: observable,
      error: observable,
      loadInvitations: action,
      sendInvitation: action,
      revokeInvitation: action,
      loadPreview: action,
      acceptInvitation: action,
      clear: action,
    });
  }

  async loadInvitations(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const list = await businessService.listInvitations();
      runInAction(() => {
        this.invitations = list;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err as ApiError;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async sendInvitation(req: CreateInvitationRequest): Promise<BusinessInvitation> {
    this.sending = true;
    this.error = null;
    try {
      const next = await businessService.createInvitation(req);
      runInAction(() => {
        this.invitations = [next, ...this.invitations];
      });
      return next;
    } catch (err) {
      runInAction(() => {
        this.error = err as ApiError;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.sending = false;
      });
    }
  }

  async revokeInvitation(invitationId: string): Promise<void> {
    this.revoking = true;
    this.error = null;
    try {
      await businessService.revokeInvitation(invitationId);
      // Refetch from backend rather than trusting an optimistic patch. The old
      // implementation only updated local state, so any backend-side mismatch
      // (wrong id, no-op response, etc.) was invisible — the row stayed
      // marked REVOKED in the UI even when the server kept it PENDING.
      const list = await businessService.listInvitations();
      runInAction(() => {
        this.invitations = list;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err as ApiError;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.revoking = false;
      });
    }
  }

  async loadPreview(token: string): Promise<InvitationPreview | null> {
    this.previewing = true;
    this.error = null;
    try {
      const res = await invitationService.preview(token);
      runInAction(() => {
        this.preview = res;
      });
      return res;
    } catch (err) {
      runInAction(() => {
        this.error = err as ApiError;
      });
      return null;
    } finally {
      runInAction(() => {
        this.previewing = false;
      });
    }
  }

  async acceptInvitation(token: string): Promise<AcceptInvitationResult> {
    this.accepting = true;
    this.error = null;
    try {
      return await invitationService.accept(token);
    } catch (err) {
      runInAction(() => {
        this.error = err as ApiError;
      });
      throw err;
    } finally {
      runInAction(() => {
        this.accepting = false;
      });
    }
  }

  clear(): void {
    this.invitations = [];
    this.preview = null;
    this.error = null;
  }
}
